import React, { useCallback, useState } from 'react';
import { ArrowRight, Plus, X } from 'lucide-react';
import { uniqBy } from 'lodash';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { cn } from '../../ui/utils';
import { useDeleteBadgeFromModal, useGetAllBadges, useUpdateBadgesFromModal } from '../../../queries';
import { useAuth } from '../../../hooks';
import { useToast } from '../../../hooks/useToast';

function BadgeItem({ badgeItem, onBadgeSelection, onDeleteBadge }) {
  const { name, badge, isSelected } = badgeItem;

  return (
    <div
      className={cn(
        'flex items-stretch overflow-hidden rounded-lg border text-sm transition-colors',
        isSelected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border/70 bg-muted/40 text-foreground'
      )}
    >
      <span className="flex min-w-0 flex-1 items-center truncate px-3 py-2.5 font-medium">
        {name}
      </span>
      <button
        type="button"
        aria-label={isSelected ? `Remove ${name}` : `Add ${name}`}
        className={cn(
          'flex shrink-0 items-center justify-center border-l px-2.5 transition-colors',
          isSelected
            ? 'border-primary-foreground/20 hover:bg-primary-foreground/10'
            : 'border-border/70 hover:bg-muted'
        )}
        onClick={() => (isSelected ? onDeleteBadge(badge) : onBadgeSelection(badge))}
      >
        {isSelected ? (
          <X className="h-4 w-4" aria-hidden />
        ) : (
          <Plus className="h-4 w-4" aria-hidden />
        )}
      </button>
    </div>
  );
}

export default function BadgeModal({ isBadgeOpen, onBadgeClose }) {
  const [userBadges, setUserBadges] = useState([]);
  const { mutate: updateBadges } = useUpdateBadgesFromModal();
  const { mutate: deleteBadge } = useDeleteBadgeFromModal();
  const { data: allBadges, isLoading } = useGetAllBadges();
  const { user } = useAuth();
  const toast = useToast();

  React.useEffect(() => {
    if (!isLoading) {
      const uniqueBadges = uniqBy(allBadges, 'name');
      setUserBadges(uniqueBadges);
    }
  }, [isLoading, allBadges]);

  const onAddBadge = useCallback(
    (badge) => {
      const badges = userBadges.map((badgeItem) => {
        if (badgeItem.badge === badge) {
          badgeItem.isSelected = true;
        }
        return badgeItem;
      });
      setUserBadges(badges);
    },
    [userBadges]
  );

  const onDeleteBadge = useCallback(
    async (badge) => {
      deleteBadge(badge);
      const badges = userBadges.map((badgeItem) => {
        if (badgeItem.badge === badge) {
          badgeItem.isSelected = false;
        }
        return badgeItem;
      });
      setUserBadges(badges);
    },
    [userBadges, deleteBadge]
  );

  const onContinue = useCallback(async () => {
    const badges = userBadges.filter((badgeItem) => badgeItem.isSelected).map((bd) => bd.badge);
    updateBadges(badges.toString());
    onBadgeClose();
    toast({
      title: 'Badges updated',
      description: "We've updated the badge list",
      status: 'success',
      duration: 9000,
      isClosable: true
    });
  }, [updateBadges, toast, onBadgeClose, userBadges]);

  const greeting = user?.displayName ? `Hi ${user.displayName}, ` : '';

  return (
    <Dialog
      open={isBadgeOpen}
      onOpenChange={(open) => {
        if (!open) onBadgeClose();
      }}
    >
      <DialogContent
        className="flex max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0"
        showCloseButton
      >
        <div className="border-b border-border/60 px-6 pb-5 pt-6 text-center">
          <img
            src="/assets/images/logo.png"
            alt="Classwise"
            className="mx-auto h-10 w-auto object-contain"
          />
          <DialogTitle className="mt-4 text-xl font-semibold text-foreground">
            {greeting}Choose your interests
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-muted-foreground">
            Select the topics you care about so we can personalise your experience.
          </DialogDescription>
        </div>

        <div className="min-h-[200px] max-h-[min(50vh,360px)] overflow-y-auto px-6 py-5">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading interests…</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {userBadges?.map((badgeItem) => (
                <BadgeItem
                  key={badgeItem.badge}
                  badgeItem={badgeItem}
                  onBadgeSelection={onAddBadge}
                  onDeleteBadge={onDeleteBadge}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-center border-t border-border/60 px-6 py-5">
          <Button
            type="button"
            size="lg"
            className="min-w-[160px] gap-2"
            disabled={!Reflect.ownKeys(userBadges).length}
            onClick={onContinue}
          >
            Continue
            <ArrowRight className="h-5 w-5" aria-hidden />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
